import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {FrameData} from "types/Frame";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import FrameForm from "pages/Frame/Form";

function MutateFrame() {
    const {id} = useParams()
    const {data: frame, isLoading} = useQuery(
        ['frames', {id}],
        () => ngage.frames.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: FrameData) => ngage.frames.store(data))
    const update = useMutation((data: FrameData) => ngage.frames.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: FrameData, {setSubmitting}: FormikHelpers<FrameData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('frames.all')

        setSubmitting(false)
        navigate('/frames')
    }

    // Handle store mutation
    async function handleStore(values: FrameData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Photo Frame Created", `${values.name} has been created`)
        } catch (error) {
            notify("error", "Photo Frame Not Created", `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: FrameData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Photo Frame Updated", `${values.name} has been updated`)
            await invalidate(['frames', {id}])
        } catch (error) {
            notify("error", "Photo Frame Not Updated", `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {
        'App Content': '/',
        'Interactive': '/',
        'Photo Frames': '/frames',
        'New Photo Frame': '/frames/new'
    }

    const editBreadcrumbs = {
        'App Content': '/',
        'Interactive': '/',
        'Photo Frames': '/frames',
        'Edit Photo Frame': `/frames/${id}/edit`
    }

    return (
        <Page title={id ? "Edit Photo Frame" : "New Photo Frame"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <FrameForm frame={frame} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateFrame;